// src/utils/skillGroupEvaluator.js

import { skillScoreMap } from "../constants/skillLevels";

/**
 * 年度を取得する（4月始まり）
 */
const getFiscalYear = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 0-indexed, so add 1
  return month >= 4 ? year : year - 1;
};

/**
 * 条件に基づいてスタッフを評価し、条件を満たすスタッフのID一覧を返す
 * @param {Object} condition - 評価条件オブジェクト
 * @param {Array} staffList - スタッフリスト
 * @param {Array} skillRecords - スキルレコード
 * @param {Array} departments - 診療科リスト
 * @param {Array} procedures - 術式リスト
 * @param {number} currentYear - 現在の年度（4月始まり）
 * @returns {Array} 条件を満たすスタッフのemployeeId一覧
 */
export const evaluateCondition = (condition, staffList, skillRecords, departments, procedures, currentYear) => {
  console.log("評価条件:", condition);
  console.log("スタッフ数:", staffList.length);
  console.log("スキルレコード数:", skillRecords.length);
  console.log("現在年度:", currentYear);

  if (!condition || !condition.type) {
    console.error("条件が無効です");
    return [];
  }

  switch (condition.type) {
    case "dept":
      return evaluateDeptCondition(condition, staffList, skillRecords, departments, procedures);
    case "procedure":
      return evaluateProcedureCondition(condition, staffList, skillRecords, procedures);
    case "experience":
      return evaluateExperienceCondition(condition, staffList, currentYear);
    case "schoolYear":
      return evaluateSchoolYearCondition(condition, staffList, currentYear);
    case "manual":
      return condition.manualMembers || [];
    default:
      console.error("不明な条件タイプ:", condition.type);
      return [];
  }
};

/**
 * 診療科条件を評価する
 */
const evaluateDeptCondition = (condition, staffList, skillRecords, departments, procedures) => {
  // 診療科名からIDを取得
  const department = departments.find(d => d.name === condition.department);
  if (!department) {
    console.error("指定された診療科が見つかりません:", condition.department);
    return [];
  }
  
  console.log("🏥 診療科ID:", department.id);
  
  // 診療科IDに紐づく術式を取得
  const relevantProcedures = procedures.filter(p => p.department_id === department.id);
  const procedureIds = relevantProcedures.map(p => p.id);
  
  console.log("📋 該当術式数:", procedureIds.length);
  console.log("📋 術式ID一覧:", procedureIds.slice(0, 5)); // 最初の5つだけ表示
  
  if (procedureIds.length === 0) {
    console.error("該当診療科の術式がありません:", department.name);
    return [];
  }
  
  // acceptableLevelsのスコアを取得
  const acceptableLevelScores = (condition.acceptableLevels || []).map(
    levelName => {
      const score = skillScoreMap[levelName];
      console.log(`levelName: "${levelName}" -> score: ${score}`);
      return score;
    }
  ).filter(score => score !== undefined);
  
  console.log("acceptableLevelScores:", acceptableLevelScores);
  
  if (acceptableLevelScores.length === 0) {
    console.error("有効なレベルが指定されていません");
    return [];
  }
  
  const minAcceptableScore = Math.min(...acceptableLevelScores);
  
  // スタッフごとの対象スキルを評価
  const results = [];
  
  staffList.forEach(staff => {
    const staffId = staff.employee_id;
    
    // staffIdに関連するスキルレコードをフィルタリング
    const staffSkills = skillRecords.filter(record => {
      const recordUserId = String(record.user_id);  // 文字列に変換
      const staffIdStr = String(staffId);            // 文字列に変換
      return recordUserId === staffIdStr && procedureIds.includes(record.procedure_id);
    });
    
    console.log(`👤 ${staff.last_name} ${staff.first_name} (${staffId}): ${staffSkills.length}件のスキルレコード`);
    
    // 条件を満たすスキル（指定レベル以上）をカウント
    const qualifiedSkills = staffSkills.filter(skill => 
      acceptableLevelScores.includes(skillScoreMap[skill.level])
    );
    
    console.log(`  ✅ 独り立ち以上: ${qualifiedSkills.length}/${procedureIds.length}`);
    
    // 必要な術式のうち、何割習得しているか
    const ratio = procedureIds.length > 0 ? qualifiedSkills.length / procedureIds.length : 0;
    const minRatio = condition.minRatio !== undefined ? condition.minRatio : 0.01;
    
    console.log(`  📊 達成率: ${(ratio * 100).toFixed(1)}% (必要: ${(minRatio * 100).toFixed(0)}%)`);
    
    if (ratio >= minRatio) {
      console.log(`  ✅ 条件満たす！`);
      results.push(staff.employee_id);
    }
  });
  
  console.log(`診療科条件評価結果 (${condition.department}):`, results);
  return results;
};

/**
 * 術式条件を評価する
 */
const evaluateProcedureCondition = (condition, staffList, skillRecords, procedures) => {
  // 術式名から術式IDを取得
  const procedure = procedures.find(p => p.name === condition.procedureName);
  if (!procedure) {
    console.error("指定された術式が見つかりません:", condition.procedureName);
    return [];
  }
  
  // acceptableLevelsのスコアを取得
  const acceptableLevelScores = (condition.acceptableLevels || []).map(
    levelName => skillScoreMap[levelName]
  ).filter(score => score !== undefined);
  
  if (acceptableLevelScores.length === 0) {
    console.error("有効なレベルが指定されていません");
    return [];
  }
  
  const minAcceptableScore = Math.min(...acceptableLevelScores);
  
  const results = [];
  
  staffList.forEach(staff => {
    const staffId = staff.employee_id;  // ← 修正
    
    // roleに応じてチェック
    const roles = condition.role === "both" 
      ? ["scrub", "circulating"] 
      : [condition.role || "scrub"];
    
    let matched = false;
    
    for (const role of roles) {
      // 該当する術式のスキルレコードを検索
      const skillRecord = skillRecords.find(record => 
        record.user_id === staffId &&  // ← 修正
        record.procedure_id === procedure.id &&  // ← 修正
        record.role === role
      );
      
      if (skillRecord && acceptableLevelScores.includes(skillScoreMap[skillRecord.level])) {
        matched = true;
        break;
      }
    }
    
    if (matched) {
      results.push(staff.employee_id);
    }
  });
  
  console.log(`術式条件評価結果 (${condition.procedureName}):`, results);
  return results;
};

/**
 * 経験年数条件を評価する（年度ベース）
 */
const evaluateExperienceCondition = (condition, staffList, currentYear) => {
  const results = [];
  
  staffList.forEach(staff => {
    const entryYear = parseInt(staff.year);
    if (isNaN(entryYear)) return;
    
    // 年度ベースで経験年数を計算（入職年度を1年目とカウント）
    const experience = currentYear - entryYear + 1;
    
    const minExp = condition.minExperience !== undefined ? condition.minExperience : 1;
    const maxExp = condition.maxExperience !== undefined ? condition.maxExperience : 20;
    
    if (experience >= minExp && experience <= maxExp) {
      results.push(staff.employee_id);
    }
  });
  
  console.log(`経験年数条件評価結果 (${condition.minExperience || 1}年目以上 ${condition.maxExperience || 20}年目以下、現在年度: ${currentYear}):`, results);
  return results;
};

/**
 * 学年条件を評価する（年度ベース）
 */
const evaluateSchoolYearCondition = (condition, staffList, currentYear) => {
  const targetYear = condition.targetSchoolYear;
  if (!targetYear) {
    console.error("対象学年が指定されていません");
    return [];
  }
  
  const results = [];
  
  staffList.forEach(staff => {
    const entryYear = parseInt(staff.year);
    if (isNaN(entryYear)) return;
    
    // 年度ベースで学年を計算（入職年度を1年目とカウント）
    const schoolYear = currentYear - entryYear + 1;
    
    if (schoolYear === targetYear) {
      results.push(staff.employee_id);
    }
  });
  
  console.log(`学年条件評価結果 (${targetYear}年目、現在年度: ${currentYear}):`, results);
  return results;
};